import React from 'react';
import PropTypes from 'prop-types';
import { Tag, Tooltip } from 'antd';
import { captureAnalytics } from '../../actions/preload';
import { patientLastNameForSearch } from '../../containers/records/shared/patientLastNameForSearch';
import './DedupBadge.scss';

export const receivedDobLastNameSearchPath = (
  record,
  containsOverride,
  includeArchived = false,
) => {
  let dateOfBirth = '';
  if (record.patient_dob) {
    const [month, day, year] = record.patient_dob.split('/');
    dateOfBirth = `${year}-${month}-${day}`;
  }

  const containsTerm = containsOverride || patientLastNameForSearch(record.patient_name);
  const containsParam = containsTerm ? `&contains=${encodeURIComponent(containsTerm)}` : '';
  // The matching record is usually the original refill, which auto-upload
  // typically archives after tasking. Include archived records so it remains visible.
  const archivedParam = includeArchived ? '&archived=both' : '';
  return `/app/received?dob=${dateOfBirth}${containsParam}${archivedParam}&non_referrals=true&show_referral_status=true&page=1`;
};

export const buildDedupBadgePresentation = (badge, record) => {
  if (!badge || badge.decision !== 'skipped') return null;

  const isUploadSkip = badge.delivery_type === 'upload';
  const matchedAt = badge.sibling_delivered_at || badge.sibling_tasked_at;
  const matchedAction = badge.sibling_delivered_at ? 'uploaded' : 'tasked';
  const hasMatchDetails = Boolean(badge.med && matchedAt);
  const skippedAction = isUploadSkip ? 'this automatic upload was skipped' : 'no new task was created';
  const fallbackAction = isUploadSkip ? 'upload it manually as usual' : 'task it as usual';
  const matchDescription = hasMatchDetails
    ? `${badge.med} was already ${matchedAction} on ${matchedAt}`
    : 'A matching request was already uploaded or tasked';
  const tooltip = [
    `${matchDescription}, so ${skippedAction}.`,
    `Click to see the matching records. If this is a duplicate, archive it; if not, ${fallbackAction}.`,
  ].join(' ');

  return {
    analyticsEvent: isUploadSkip
      ? 'upload_dedup_matching_records_opened'
      : 'task_dedup_matching_records_opened',
    hasMatchDetails,
    href: receivedDobLastNameSearchPath(record, badge.med, true),
    label: isUploadSkip
      ? 'Upload skipped — possible duplicate'
      : 'Task skipped — possible duplicate',
    tooltip,
  };
};

const DedupBadge = ({ badge, record }) => {
  const presentation = buildDedupBadgePresentation(badge, record);
  if (!presentation) return null;

  return (
    <div className="task-dedup-badge">
      <Tooltip title={presentation.tooltip}>
        <a
          className="task-dedup-badge__link"
          href={presentation.href}
          onClick={(event) => {
            event.stopPropagation();
            captureAnalytics(presentation.analyticsEvent, {
              has_match_details: presentation.hasMatchDetails,
            });
          }}
        >
          <Tag color="volcano" style={{ cursor: 'pointer' }}>{presentation.label}</Tag>
        </a>
      </Tooltip>
    </div>
  );
};

DedupBadge.propTypes = {
  badge: PropTypes.shape({
    decision: PropTypes.string,
    delivery_type: PropTypes.string,
    med: PropTypes.string,
    sibling_delivered_at: PropTypes.string,
    sibling_tasked_at: PropTypes.string,
  }),
  record: PropTypes.shape({
    patient_dob: PropTypes.string,
    patient_name: PropTypes.string,
  }).isRequired,
};

DedupBadge.defaultProps = {
  badge: null,
};

export default DedupBadge;
